/* The colour pull (site-furniture spec §5.5). It starts from the artwork's land alone; each band then rises out of
   the land at its own pace, and the palette dots appear. Everything comes from the face image itself: above the land
   each column is one colour, so the top of a column's land is where it stops matching its top pixel, and a median
   over 7 columns smooths that edge where a band's colour meets the hills. The paces come from a hash of each band's
   number, so they're uneven but the same on every visit. It plays once, when in view. With reduced motion, or where
   the image's pixels can't be read, the finished artwork shows. */
(() => {
  const HOLD = 600, RISE = 2200, SPREAD = 900, DOTS = 3400, END = 3800;   // ms
  const ease = (t) => (t <= 0 ? 0 : t >= 1 ? 1 : 1 - (1 - t) ** 3);
  const pace = (n) => { const s = Math.sin(n * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s); };   // 0 to 1

  function columns(px, W, H) {
    const at = (x, y) => (y * W + x) * 4;
    const near = (i, j, tolerance) => Math.abs(px[i] - px[j]) + Math.abs(px[i + 1] - px[j + 1]) + Math.abs(px[i + 2] - px[j + 2]) < tolerance;
    const cols = [];
    let band = 0;
    for (let x = 0; x < W; x++) {
      const top = at(x, 2);
      let y = 3, miss = 0;
      for (; y < H; y++) {
        if (near(at(x, y), top, 40)) miss = 0;
        else if (++miss > 3) { y -= 3; break; }
      }
      if (x > 0 && !near(top, at(x - 1, 2), 24)) band += 1;
      cols.push({ land: y, delay: pace(band) });
    }
    const raw = cols.map((c) => c.land);
    cols.forEach((c, x) => {
      const around = raw.slice(Math.max(0, x - 3), x + 4).sort((a, b) => a - b);
      c.land = around[Math.floor(around.length / 2)];
    });
    return cols;
  }

  function setup(section) {
    if (section.dataset.grPull) return;
    const img = section.querySelector('.gr-pull__img'), canvas = section.querySelector('.gr-pull__canvas');
    const dots = section.querySelector('.gr-pull__dots'), art = section.querySelector('.gr-pull__art');
    const plain = () => { dots?.classList.add('is-on'); section.dataset.grPull = 'plain'; };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { plain(); return; }
    section.dataset.grPull = 'loading';
    const start = () => {
      const W = Math.min(600, img.naturalWidth), H = Math.round(W * img.naturalHeight / img.naturalWidth);
      canvas.width = W;
      canvas.height = H;
      const cx = canvas.getContext('2d');
      cx.drawImage(img, 0, 0, W, H);
      let cols;
      try { cols = columns(cx.getImageData(0, 0, W, H).data, W, H); } catch (e) { plain(); return; }
      const sky = getComputedStyle(art).backgroundColor || '#f3ece2';
      const draw = (t) => {
        cx.drawImage(img, 0, 0, W, H);
        cx.fillStyle = sky;
        cols.forEach((c, x) => {
          const cover = c.land * (1 - ease((t - HOLD - c.delay * SPREAD) / RISE));
          if (cover > 0.5) cx.fillRect(x, 0, 1, cover);
        });
      };
      draw(0);
      canvas.hidden = false;
      img.style.opacity = '0';                           // kept in place for its size and its alt text
      section.dataset.grPull = 'ready';
      new IntersectionObserver(([entry], watcher) => {
        if (!entry.isIntersecting) return;
        watcher.disconnect();
        section.dataset.grPull = 'playing';
        let t0 = null;
        const frame = (now) => {
          if (t0 === null) t0 = now;
          const t = now - t0;
          draw(t);
          if (t > DOTS) dots?.classList.add('is-on');
          if (t < END) { requestAnimationFrame(frame); return; }
          cx.drawImage(img, 0, 0, W, H);
          dots?.classList.add('is-on');
          section.dataset.grPull = 'done';
        };
        requestAnimationFrame(frame);
      }, { threshold: 0.55 }).observe(canvas);
    };
    if (img.complete && img.naturalWidth) start();
    else if (img.complete) plain();                      // it already failed to load
    else {
      img.addEventListener('load', start, { once: true });
      img.addEventListener('error', plain, { once: true });
    }
  }

  document.querySelectorAll('[data-gr-pull]').forEach(setup);
})();
